import { INVITE_CODE_LENGTH, parseInviteCode, type PartyMember, type RunListing } from '@d2/schema';

import { type RunRefusalReason } from '../game/runs';

/**
 * The lobby's contract, its words, and the two decisions it is allowed to make.
 *
 * ## Not one rule is decided here, and there is barely one to decide
 *
 * Everything on the list arrives already decided by the server: which runs are
 * offered at all (`apps/game`'s `listRuns` — public, not full, not empty, with a
 * code), how deep each one is, and who is in it. This file arranges those facts
 * into sentences. It does not filter the list, does not sort it, and above all
 * does not decide whether a run can be joined — that is answered at the moment
 * of the click, by the server, against the party as it stands *then*
 * (`game/runs.ts`'s `addressOf`).
 *
 * The one piece of validation that does happen here is **not this file's
 * rule**: `parseInviteCode` is `@d2/schema`'s, the same function the server
 * parses the path segment with. Calling it is what makes a typo an instant
 * answer instead of a round trip, and writing a second, looser or stricter
 * version of it in this package is the failure it exists to prevent — a client
 * that nags about a code the server would have accepted, or accepts one it
 * would not.
 *
 * ## The refusals are the feature
 *
 * A directory that is empty most of the time and refuses most of the rest is a
 * feature made almost entirely of sentences about things not happening. Four of
 * the five below are a **healthy server answering precisely**: a party that
 * filled, a run that ended, a code that was mistyped, a session that expired.
 * Exactly one is an outage. This repo has now reported a healthy server as an
 * outage three separate times (`game/join-refusal.ts`), and every one of those
 * was a collapse of the shape *"several answers became `unreachable`"* — so the
 * list is closed, the sentences are distinct, and a test walks all five.
 */

/**
 * Every reason a directory read can come back refused, for a caller that has to
 * be exhaustive about them.
 *
 * Written out rather than derived from `@d2/schema`'s `RUN_REFUSALS` plus one,
 * so that a test which walks this is comparing two definitions instead of one
 * with itself — the same reason `JOIN_REFUSALS` is written out.
 */
export const RUN_REFUSAL_REASONS = Object.freeze([
  'unauthorised',
  'malformed-code',
  'no-such-run',
  'run-full',
  'unreachable',
] as const);

/**
 * What a player is told about each of them.
 *
 * Not one repeats the server's own word: `run-full` is a wire token, *"that
 * party is full"* is a sentence, and the difference is the whole reason the
 * refusal is carried as a value and turned into words here. Each names **what a
 * player can do about it**, because that is what separates the ones that look
 * alike from a distance — a full party is waited out, an ended run is replaced,
 * a typo is retyped, and an outage is none of the three.
 */
export const describeRunRefusal = (reason: RunRefusalReason): string => {
  switch (reason) {
    case 'run-full':
      return 'That party is full. Try again in a moment — a seat frees up when somebody leaves.';
    case 'no-such-run':
      return 'That run has ended. Refresh the list, or ask for a code to a run that is still going.';
    case 'malformed-code':
      return `That is not a code. A code is ${INVITE_CODE_LENGTH} letters and digits, as your friend read them out.`;
    case 'unauthorised':
      return 'This session has ended. Sign in again to keep playing.';
    case 'unreachable':
      return 'The game server is not answering. Try again in a moment.';
  }
};

/** Said while the directory is being read. */
export const READING_NOTE = 'Looking for open runs…';

/** Said while a code is being turned into somewhere to join. */
export const ASKING_NOTE = 'Asking where that run is…';

/**
 * Said when nothing is open — **the ordinary case**, not a failure.
 *
 * A game with three characters in its database has an empty lobby almost
 * always, so this sentence is the one a player will read most often and it must
 * not read like something went wrong. It names both ways on from an empty list,
 * because both are on this very screen: the Descend button above it, and the
 * code field beside it.
 */
export const NOTHING_OPEN =
  'No public runs are open just now. Press Descend to start one — or type a friend’s invite code below.';

/**
 * Why a friend's run may not be on the list.
 *
 * Said out loud rather than left to be discovered: a private run is *unlisted*,
 * not unjoinable (`apps/game`'s `resolveRun` does not consult visibility at
 * all), and a player who could not find their friend's run would otherwise
 * conclude the lobby is broken.
 */
export const PRIVATE_NOTE = 'A private run is never listed. Its code is the only way in.';

/** What the field is for. */
export const CODE_LABEL = 'Invite code';

/** What the plate over the world calls the code this run is invited by. */
export const INVITE_LABEL = 'Invite code';

/**
 * What is wrong with what has been typed so far, or nothing at all.
 *
 * Instant feedback, and the *absence* of it is the load-bearing half: this must
 * stay silent for every spelling `parseInviteCode` accepts. A code is meant to
 * be read out loud and typed back in, so lower case, a hyphen in the middle, a
 * space and an `O` where a zero was are all the same code — and a hint that
 * nagged about any of them would be a second, stricter definition of a code
 * living in a panel.
 *
 * It is silent for an empty field too. Nothing has been typed, so nothing is
 * wrong.
 */
export const codeHintOf = (typed: string): string => {
  if (typed.trim() === '') return '';

  return parseInviteCode(typed) === null ? describeRunRefusal('malformed-code') : '';
};

/** Whether a join is already on its way. The code being resolved, or nothing. */
export const isJoining = (joining: string | null | undefined): boolean =>
  joining !== null && joining !== undefined;

/**
 * The join to start, or `null` because one is already in flight.
 *
 * **Any** join blocks any other, not merely a second press of the same row: two
 * seat reservations for one character is what the room answers 409
 * `character-already-on-this-floor` (`game/join-refusal.ts`), and a player who
 * pressed two rows in one second would be shown that refusal about their own
 * client's doing.
 */
export const startJoin = (joining: string | null | undefined, code: string): string | null =>
  isJoining(joining) ? null : code;

/**
 * The one line the lobby says out loud.
 *
 * A join in flight wins over the news of the last one, for the reason
 * `statusNoteOf` gives about a run that is opening: the press that produced this
 * render *is* the answer to whatever came before it, and two live sentences in
 * one polite region are read out as a single run-on line.
 *
 * Empty string rather than `null`, so the region keeps its identity across
 * renders and a reader actually announces it.
 */
export const lobbyStatusOf = (
  joining: string | null | undefined,
  note: RunRefusalReason | null | undefined,
): string => {
  if (isJoining(joining)) return ASKING_NOTE;

  return note === null || note === undefined ? '' : describeRunRefusal(note);
};

/** Which floor, and how full — the two numbers a player chooses on. */
export const runSummary = (run: RunListing): string => `Floor ${run.depth} · ${run.players}/${run.maxPlayers}`;

/**
 * Who is in it, by name and level.
 *
 * Both facts, because they answer different questions: the name is *"that is my
 * friend's character"* and the level is what tells a level 4 they would be
 * joining a level 30's floor 12.
 */
export const partyLabel = (party: readonly PartyMember[]): string =>
  party.length === 0
    ? 'Nobody yet'
    : party.map((member) => `${member.name} (${member.level})`).join(', ');

/** The whole run in one line, for a reader who is given no table to scan. */
export const describeRun = (run: RunListing): string =>
  `${runSummary(run)} — ${partyLabel(run.party)}`;

/**
 * A row's control, by its accessible name.
 *
 * The floor and the party rather than the code: fifteen buttons reading *Join*
 * name nothing, and the code is machinery — it is what the click *sends*, not
 * what the click is a decision about. It is deliberately not read out, because
 * a public run's code is not a secret but it is also not information a player
 * choosing between two runs has any use for.
 */
export const joinName = (run: RunListing): string => `Join ${describeRun(run)}`;
